/** Mobile D-Pad: layout, touch input, settings preview, and customize mode. */

import { syncToggleSwitch } from "./toggle-switch";

export type DpadDir = "up" | "down" | "left" | "right";
export type DpadDiagDir = "up-left" | "up-right" | "down-left" | "down-right";
export type DpadButtonId = DpadDir | DpadDiagDir;
export type DpadLayoutId = 1 | 2 | 3;

export const DPAD_INSET_R_KEY = "dpadInsetRight";
export const DPAD_INSET_B_KEY = "dpadInsetBottom";
export const DPAD_INSET_L_KEY = "dpadInsetLeft";
export const DPAD_LAYOUT_KEY = "dpadLayout";
export const DPAD_OFFSETS_KEY = "dpadButtonOffsets";
export const DPAD_LAYOUT3_POS_KEY = "dpadLayout3Pos";
export const DPAD_EIGHT_DIR_KEY = "settingDpadEightDirections";
export const DPAD_SCALE_KEY = "dpadButtonScale";
export const DPAD_OPACITY_KEY = "dpadButtonOpacity";
export const DPAD_GRID_KEY = "settingDpadGridSnap";

export const dpadHold: Record<DpadDir, boolean> = { up: false, down: false, left: false, right: false };

export let dpadPositionCustomizeMode = false;
export let settingDpadEightDirections = true;

const DPAD_DIRS: DpadDir[] = ["up", "down", "left", "right"];
const DPAD_DIAG_DIRS: DpadDiagDir[] = ["up-left", "up-right", "down-left", "down-right"];
const ALL_BUTTON_IDS: DpadButtonId[] = [...DPAD_DIRS, ...DPAD_DIAG_DIRS];

const DIAG_TO_CARDINAL: Record<DpadDiagDir, [DpadDir, DpadDir]> = {
    "up-left": ["up", "left"],
    "up-right": ["up", "right"],
    "down-left": ["down", "left"],
    "down-right": ["down", "right"],
};


const HIT_EXPAND_PX = 12;
const LAYOUT3_BTN_PX = 52;
const LAYOUT3_GAP_PX = 8;
const LAYOUT3_SAFE_PX = 12;
const DPAD_SCALE_MIN = 0.65;
const DPAD_SCALE_MAX = 1.55;
const DPAD_SCALE_DEFAULT = 1;
const DPAD_OPACITY_MIN = 0.2;
const DPAD_OPACITY_MAX = 1;
const DPAD_OPACITY_DEFAULT = 1;
const CUSTOMIZE_DRAG_THRESHOLD_PX = 14;
const DPAD_BUTTON_LABELS: Record<DpadButtonId, string> = {
    up: "▲ Up",
    down: "▼ Down",
    left: "◀ Left",
    right: "▶ Right",
    "up-left": "↖ Up-left",
    "up-right": "↗ Up-right",
    "down-left": "↙ Down-left",
    "down-right": "↘ Down-right",
};

let positionPanelSyncing = false;
let positionInputUndoArmed = false;

type DpadDragMode = "group" | "multi" | DpadButtonId;

const DPAD_CONFIG_KEYS = [
    DPAD_INSET_R_KEY,
    DPAD_INSET_B_KEY,
    DPAD_INSET_L_KEY,
    DPAD_LAYOUT_KEY,
    DPAD_OFFSETS_KEY,
    DPAD_LAYOUT3_POS_KEY,
    DPAD_EIGHT_DIR_KEY,
    DPAD_SCALE_KEY,
    DPAD_OPACITY_KEY,
    DPAD_GRID_KEY,
];

let dpadCustomizeSnapshot: Record<string, string | null> | null = null;
let dpadUndoStack: Record<string, string | null>[] = [];
let dpadRedoStack: Record<string, string | null>[] = [];
let dpadDragUndoPushed = false;
let dpadPinchUndoPushed = false;

const pendingCustomizeDrag = {
    active: false,
    pointerId: -1,
    mode: null as "group" | DpadButtonId | null,
    startX: 0,
    startY: 0,
};

type OffsetMap = Partial<Record<DpadButtonId, { x: number; y: number }>>;
type Layout3Pos = Partial<Record<DpadButtonId, { left?: number; right?: number; bottom: number }>>;

const dpadDrag = {
    active: false,
    pointerId: -1,
    mode: "group" as DpadDragMode,
    startX: 0,
    startY: 0,
    startRight: 0,
    startBottom: 0,
    startLeft: 0,
    startOffsets: {} as OffsetMap,
    startLayout3: {} as Layout3Pos,
};

let dpadMultiSelectMode = false;
const selectedDpadButtons = new Set<DpadButtonId>();

const marqueeSelect = {
    active: false,
    dragging: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
};

const dpadPointerHolds = new Map<number, Record<DpadDir, boolean>>();

const dpadPinch = {
    active: false,
    startDist: 0,
    startScale: DPAD_SCALE_DEFAULT,
    currentScale: DPAD_SCALE_DEFAULT,
    pointers: new Map<number, { x: number; y: number }>(),
};

let dpadPlayGate: () => boolean = () => false;
let dpadTouchUiGate: () => boolean = () => false;

function clamp(n: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, n));
}

function getBoolSetting(key: string, fallback: boolean) {
    const v = localStorage.getItem(key);
    if (v === "true") return true;
    if (v === "false") return false;
    return fallback;
}

function readScale(): number {
    const v = Number(localStorage.getItem(DPAD_SCALE_KEY) ?? String(DPAD_SCALE_DEFAULT));
    if (!Number.isFinite(v)) return DPAD_SCALE_DEFAULT;
    return clamp(v, DPAD_SCALE_MIN, DPAD_SCALE_MAX);
}

function applyScaleToEl(el: HTMLElement) {
    el.style.setProperty("--dpad-scale", String(readScale()));
}

function readOpacity(): number {
    const v = Number(localStorage.getItem(DPAD_OPACITY_KEY) ?? "100");
    if (!Number.isFinite(v)) return DPAD_OPACITY_DEFAULT;
    return clamp(v / 100, DPAD_OPACITY_MIN, DPAD_OPACITY_MAX);
}

function applyOpacityToEl(el: HTMLElement) {
    el.style.setProperty("--dpad-btn-opacity", String(readOpacity()));
}

function readGridEnabled(): boolean {
    return getBoolSetting(DPAD_GRID_KEY, false);
}

function captureDpadState(): Record<string, string | null> {
    const snap: Record<string, string | null> = {};
    for (const k of DPAD_CONFIG_KEYS) snap[k] = localStorage.getItem(k);
    return snap;
}

function applyDpadState(snap: Record<string, string | null>) {
    for (const k of DPAD_CONFIG_KEYS) {
        const v = snap[k];
        if (v == null) localStorage.removeItem(k);
        else localStorage.setItem(k, v);
    }
    reloadDpadSettingsFromStorage();
    applyDpadLayoutFromStorage();
    syncDpadCustomizeScaleSlider();
    syncDpadCustomizeOpacitySlider();
    refreshDpadGridBtn();
    refreshDpadGridOverlay();
    clearDpadMultiSelect();
    syncDpadCustomizeUndoRedoButtons();
}

export function pushDpadCustomizeUndoBeforeChange() {
    if (!dpadPositionCustomizeMode) return;
    dpadUndoStack.push(captureDpadState());
    if (dpadUndoStack.length > 50) dpadUndoStack.shift();
    dpadRedoStack = [];
    syncDpadCustomizeUndoRedoButtons();
}

export function undoDpadCustomize() {
    if (!dpadPositionCustomizeMode || dpadUndoStack.length === 0) return;
    dpadRedoStack.push(captureDpadState());
    applyDpadState(dpadUndoStack.pop()!);
}

export function redoDpadCustomize() {
    if (!dpadPositionCustomizeMode || dpadRedoStack.length === 0) return;
    dpadUndoStack.push(captureDpadState());
    applyDpadState(dpadRedoStack.pop()!);
}

export function syncDpadCustomizeUndoRedoButtons() {
    const undo = document.getElementById("dpadCustomizeUndoBtn") as HTMLButtonElement | null;
    const redo = document.getElementById("dpadCustomizeRedoBtn") as HTMLButtonElement | null;
    if (undo) undo.disabled = dpadUndoStack.length === 0;
    if (redo) redo.disabled = dpadRedoStack.length === 0;
}

function getDpadButtonCellSizePx(): number {
    const el = getTouchDpadEl();
    if (!el) return 52 * readScale();
    const btn = el.querySelector(".touch-dpad-up, .touch-dpad-left");
    if (btn instanceof HTMLElement) {
        const r = btn.getBoundingClientRect();
        if (r.width > 0) return r.width;
    }
    return 52 * readScale();
}

function snapPx(v: number, cell: number) {
    if (cell <= 0) return v;
    return Math.round(v / cell) * cell;
}

function usesLayout3Left(id: DpadButtonId) {
    return id === "left" || id === "right" || id === "down-left" || id === "down-right";
}

function snapLayout3ButtonToGrid(btn: HTMLElement, id: DpadButtonId, cell: number) {
    const rect = btn.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    const left = clamp(snapPx(rect.left, cell), 0, Math.max(0, innerWidth - w));
    const bottom = clamp(snapPx(innerHeight - rect.bottom, cell), 0, Math.max(0, innerHeight - h));
    btn.style.transform = "";
    btn.style.bottom = `${bottom}px`;
    if (usesLayout3Left(id)) {
        btn.style.left = `${left}px`;
        btn.style.right = "auto";
    } else {
        btn.style.right = `${innerWidth - left - w}px`;
        btn.style.left = "auto";
    }
}

export function snapAllDpadButtonsToGrid() {
    if (!readGridEnabled()) return;
    const cell = getDpadButtonCellSizePx();
    if (cell <= 0) return;
    const el = getTouchDpadEl();
    if (!el) return;
    const layout = readLayout();

    if (layout === 3) {
        for (const id of ALL_BUTTON_IDS) {
            const btn = el.querySelector(`.touch-dpad-${id}`);
            if (!(btn instanceof HTMLElement) || btn.style.display === "none") continue;
            snapLayout3ButtonToGrid(btn, id, cell);
        }
        return;
    }

    const rect = el.getBoundingClientRect();
    const h = rect.height;
    const w = rect.width;
    let bottom = snapPx(innerHeight - rect.bottom, cell);
    bottom = clamp(bottom, 0, Math.max(0, innerHeight - h));
    el.style.bottom = `${bottom}px`;
    if (layout === 2) {
        let left = snapPx(rect.left, cell);
        left = clamp(left, 0, Math.max(0, innerWidth - w));
        el.style.left = `${left}px`;
        el.style.right = "auto";
    } else {
        let right = snapPx(innerWidth - rect.right, cell);
        right = clamp(right, 0, Math.max(0, innerWidth - w));
        el.style.right = `${right}px`;
        el.style.left = "auto";
    }

    for (const id of ALL_BUTTON_IDS) {
        const btn = el.querySelector(`.touch-dpad-${id}`);
        if (!(btn instanceof HTMLElement)) continue;
        const tr = btn.style.transform;
        const m = /translate\(\s*(-?[\d.]+)px,\s*(-?[\d.]+)px\s*\)/.exec(tr);
        if (!m) {
            btn.style.transform = "";
            continue;
        }
        const x = snapPx(Number(m[1]), cell);
        const y = snapPx(Number(m[2]), cell);
        btn.style.transform = x || y ? `translate(${x}px, ${y}px)` : "";
    }
}

export function refreshDpadGridOverlay() {
    const overlay = document.getElementById("dpadCustomizeGridOverlay");
    if (!overlay) return;
    const on = dpadPositionCustomizeMode && readGridEnabled();
    overlay.classList.toggle("hidden", !on);
    if (on) overlay.style.setProperty("--dpad-grid-cell", `${getDpadButtonCellSizePx()}px`);
}

function refreshDpadGridBtn() {
    const btn = document.getElementById("dpadCustomizeGridBtn");
    if (!btn) return;
    const on = readGridEnabled();
    btn.textContent = on ? "Grid ON" : "Grid";
    btn.classList.toggle("dpad-customize-toolbar-btn--on", on);
    btn.classList.toggle("toggleOn", on);
}

export function toggleDpadGridSnap() {
    pushDpadCustomizeUndoBeforeChange();
    const on = !readGridEnabled();
    localStorage.setItem(DPAD_GRID_KEY, on ? "true" : "false");
    refreshDpadGridBtn();
    refreshDpadGridOverlay();
    if (on) {
        snapAllDpadButtonsToGrid();
        persistDpadPositionFromElement(false);
    }
}

function ensureDpadCustomizeMarqueeEl(): HTMLElement {
    const el = document.getElementById("dpadCustomizeMarquee");
    if (el) return el;
    const created = document.createElement("div");
    created.id = "dpadCustomizeMarquee";
    created.className = "dpad-customize-marquee hidden";
    created.setAttribute("aria-hidden", "true");
    document.body.appendChild(created);
    return created;
}

function updateMarqueeRect(x1: number, y1: number, x2: number, y2: number) {
    const el = ensureDpadCustomizeMarqueeEl();
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const w = Math.abs(x2 - x1);
    const h = Math.abs(y2 - y1);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.width = `${w}px`;
    el.style.height = `${h}px`;
    el.classList.toggle("hidden", w < 2 && h < 2);
}

function hideMarqueeRect() {
    const el = document.getElementById("dpadCustomizeMarquee");
    if (el) el.classList.add("hidden");
}

function rectsIntersect(a: DOMRect, b: { left: number; top: number; right: number; bottom: number }) {
    return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
}

function selectButtonsInMarquee(x1: number, y1: number, x2: number, y2: number) {
    const sel = {
        left: Math.min(x1, x2),
        top: Math.min(y1, y2),
        right: Math.max(x1, x2),
        bottom: Math.max(y1, y2),
    };
    selectedDpadButtons.clear();
    const el = getTouchDpadEl();
    if (!el) return;
    for (const id of ALL_BUTTON_IDS) {
        const btn = el.querySelector(`.touch-dpad-${id}`);
        if (!(btn instanceof HTMLElement) || btn.style.display === "none") continue;
        if (rectsIntersect(btn.getBoundingClientRect(), sel)) selectedDpadButtons.add(id);
    }
    dpadMultiSelectMode = selectedDpadButtons.size > 0;
    document.body.classList.toggle("dpad-multi-select-mode", dpadMultiSelectMode);
    syncDpadSelectionVisual();
    refreshDpadCustomizePositionPanel();
}

function cancelMarqueeSelect() {
    marqueeSelect.active = false;
    marqueeSelect.dragging = false;
    marqueeSelect.pointerId = -1;
    hideMarqueeRect();
}

export function reloadDpadSettingsFromStorage() {
    settingDpadEightDirections = getBoolSetting(DPAD_EIGHT_DIR_KEY, true);
}

export function getTouchDpadEl(): HTMLElement | null {
    const layer = document.getElementById("touchSteerLayer");
    return layer?.querySelector(".touch-dpad") ?? null;
}

function getDpadRoots(): HTMLElement[] {
    const live = getTouchDpadEl();
    return live ? [live] : [];
}

function isDpadCustomizeButtonVisible(btn: HTMLElement, id: DpadButtonId) {
    if (!settingDpadEightDirections && DPAD_DIAG_DIRS.includes(id as DpadDiagDir)) return false;
    if (btn.style.display === "none") return false;
    if (btn.classList.contains("touch-dpad-diag") && !settingDpadEightDirections) return false;
    return true;
}

function readButtonViewportPosition(btn: HTMLElement) {
    const rect = btn.getBoundingClientRect();
    return { x: Math.round(rect.left), y: Math.round(rect.top) };
}

function readBtnTransform(btn: HTMLElement): { x: number; y: number } {
    const m = /translate\(\s*(-?[\d.]+)px,\s*(-?[\d.]+)px\s*\)/.exec(btn.style.transform);
    if (m) return { x: Number(m[1]), y: Number(m[2]) };
    return { x: 0, y: 0 };
}

function applyButtonViewportPosition(id: DpadButtonId, x: number, y: number) {
    const el = getTouchDpadEl();
    if (!el) return;
    const btn = el.querySelector(`.touch-dpad-${id}`);
    if (!(btn instanceof HTMLElement) || !isDpadCustomizeButtonVisible(btn, id)) return;
    const nx = Math.round(x);
    const ny = Math.round(y);
    const layout = readLayout();
    const rect = btn.getBoundingClientRect();
    const w = rect.width || btn.offsetWidth || LAYOUT3_BTN_PX;
    const h = rect.height || btn.offsetHeight || LAYOUT3_BTN_PX;

    if (layout === 3) {
        btn.style.transform = "";
        const left = clamp(nx, 0, Math.max(0, innerWidth - w));
        const bottom = clamp(Math.round(innerHeight - ny - h), 0, Math.max(0, innerHeight - h));
        btn.style.bottom = `${bottom}px`;
        if (usesLayout3Left(id)) {
            btn.style.left = `${left}px`;
            btn.style.right = "auto";
        } else {
            btn.style.right = `${Math.max(0, Math.round(innerWidth - left - w))}px`;
            btn.style.left = "auto";
        }
        return;
    }

    const { x: tx, y: ty } = readBtnTransform(btn);
    const dx = nx - Math.round(rect.left);
    const dy = ny - Math.round(rect.top);
    btn.style.transform = `translate(${Math.round(tx + dx)}px, ${Math.round(ty + dy)}px)`;
}

function selectDpadButtonOnTap(id: DpadButtonId, additive: boolean) {
    if (additive) {
        if (selectedDpadButtons.has(id)) selectedDpadButtons.delete(id);
        else selectedDpadButtons.add(id);
    } else {
        selectedDpadButtons.clear();
        selectedDpadButtons.add(id);
    }
    dpadMultiSelectMode = selectedDpadButtons.size > 0;
    document.body.classList.toggle("dpad-multi-select-mode", dpadMultiSelectMode);
    syncDpadSelectionVisual();
    refreshDpadCustomizePositionPanel();
}

function syncDpadCustomizePositionInputs(ids?: Iterable<DpadButtonId>) {
    if (!dpadPositionCustomizeMode || positionPanelSyncing) return;
    const panel = document.getElementById("dpadCustomizePositionPanel");
    const el = getTouchDpadEl();
    if (!panel || !el) return;
    const active = document.activeElement;
    const editingRow =
        active instanceof HTMLInputElement && active.classList.contains("dpad-customize-pos-input")
            ? active.closest(".dpad-customize-position-row")
            : null;
    for (const id of ids ?? ALL_BUTTON_IDS) {
        const btn = el.querySelector(`.touch-dpad-${id}`);
        if (!(btn instanceof HTMLElement) || !isDpadCustomizeButtonVisible(btn, id)) continue;
        const row = panel.querySelector(`.dpad-customize-position-row[data-dpad-id="${id}"]`);
        if (!(row instanceof HTMLElement)) continue;
        if (editingRow === row) continue;
        const { x, y } = readButtonViewportPosition(btn);
        const xInput = row.querySelector('input[data-axis="x"]');
        const yInput = row.querySelector('input[data-axis="y"]');
        if (xInput instanceof HTMLInputElement) xInput.value = String(x);
        if (yInput instanceof HTMLInputElement) yInput.value = String(y);
        row.classList.toggle("dpad-customize-position-row--selected", selectedDpadButtons.has(id));
    }
}

function refreshDpadCustomizePositionPanel() {
    if (!dpadPositionCustomizeMode) return;
    const panel = document.getElementById("dpadCustomizePositionPanel");
    if (!panel) return;
    const el = getTouchDpadEl();
    positionPanelSyncing = true;
    panel.replaceChildren();
    if (!el) {
        positionPanelSyncing = false;
        return;
    }
    for (const id of ALL_BUTTON_IDS) {
        const btn = el.querySelector(`.touch-dpad-${id}`);
        if (!(btn instanceof HTMLElement) || !isDpadCustomizeButtonVisible(btn, id)) continue;
        const { x, y } = readButtonViewportPosition(btn);
        const row = document.createElement("div");
        row.className = "dpad-customize-position-row";
        if (selectedDpadButtons.has(id)) row.classList.add("dpad-customize-position-row--selected");
        row.dataset.dpadId = id;

        const label = document.createElement("span");
        label.className = "dpad-customize-position-label";
        label.textContent = DPAD_BUTTON_LABELS[id];

        const xField = document.createElement("label");
        xField.className = "dpad-customize-position-field";
        xField.innerHTML = `X <input type="number" class="dpad-customize-pos-input" data-axis="x" inputmode="numeric" aria-label="${DPAD_BUTTON_LABELS[id]} X">`;
        const xInput = xField.querySelector("input");
        if (xInput instanceof HTMLInputElement) xInput.value = String(x);

        const yField = document.createElement("label");
        yField.className = "dpad-customize-position-field";
        yField.innerHTML = `Y <input type="number" class="dpad-customize-pos-input" data-axis="y" inputmode="numeric" aria-label="${DPAD_BUTTON_LABELS[id]} Y">`;
        const yInput = yField.querySelector("input");
        if (yInput instanceof HTMLInputElement) yInput.value = String(y);

        row.append(label, xField, yField);
        panel.appendChild(row);
    }
    positionPanelSyncing = false;
}

function bindDpadCustomizePositionPanel() {
    const panel = document.getElementById("dpadCustomizePositionPanel");
    if (!panel || panel.dataset.bound === "1") return;
    panel.dataset.bound = "1";
    const commitRow = (row: HTMLElement) => {
        const id = row.dataset.dpadId as DpadButtonId | undefined;
        if (!id) return;
        const xEl = row.querySelector('input[data-axis="x"]');
        const yEl = row.querySelector('input[data-axis="y"]');
        if (!(xEl instanceof HTMLInputElement) || !(yEl instanceof HTMLInputElement)) return;
        const x = Number(xEl.value);
        const y = Number(yEl.value);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        if (!positionInputUndoArmed) {
            pushDpadCustomizeUndoBeforeChange();
            positionInputUndoArmed = true;
        }
        applyButtonViewportPosition(id, x, y);
        persistDpadPositionFromElement(true);
        syncDpadCustomizePositionInputs([id]);
        positionInputUndoArmed = false;
    };
    panel.addEventListener("focusin", (e) => {
        if (positionPanelSyncing) return;
        if (e.target instanceof HTMLInputElement && e.target.classList.contains("dpad-customize-pos-input")) {
            positionInputUndoArmed = false;
        }
    });
    panel.addEventListener("change", (e) => {
        if (positionPanelSyncing) return;
        const row = e.target instanceof HTMLElement ? e.target.closest(".dpad-customize-position-row") : null;
        if (row instanceof HTMLElement) commitRow(row);
    });
    panel.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") return;
        const row = e.target instanceof HTMLElement ? e.target.closest(".dpad-customize-position-row") : null;
        if (row instanceof HTMLElement) {
            e.preventDefault();
            commitRow(row);
            if (e.target instanceof HTMLInputElement) e.target.blur();
        }
    });
}

function syncDpadEightDirClass() {
    const on = settingDpadEightDirections;
    for (const el of getDpadRoots()) {
        el.classList.toggle("touch-dpad--eight-dir", on);
    }
}

function getDpadButtons(root?: HTMLElement | null): HTMLElement[] {
    const el = root ?? getTouchDpadEl();
    if (!el) return [];
    return [...el.querySelectorAll(".touch-dpad-btn[data-dpad], .touch-dpad-btn[data-dpad-diag]")].filter(
        (b): b is HTMLElement => b instanceof HTMLElement,
    );
}

function buttonIdFromEl(btn: HTMLElement): DpadButtonId | null {
    const d = btn.getAttribute("data-dpad");
    if (d && DPAD_DIRS.includes(d as DpadDir)) return d as DpadDir;
    const diag = btn.getAttribute("data-dpad-diag");
    if (diag && DPAD_DIAG_DIRS.includes(diag as DpadDiagDir)) return diag as DpadDiagDir;
    return null;
}

function readLayout(): DpadLayoutId {
    const v = Number(localStorage.getItem(DPAD_LAYOUT_KEY) ?? "1");
    if (v === 2 || v === 3) return v;
    return 1;
}

function readOffsets(): OffsetMap {
    try {
        const raw = localStorage.getItem(DPAD_OFFSETS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function persistOffsets(offsets: OffsetMap) {
    const clean: OffsetMap = {};
    for (const id of ALL_BUTTON_IDS) {
        const o = offsets[id];
        if (o && (o.x !== 0 || o.y !== 0)) clean[id] = { x: o.x, y: o.y };
    }
    if (Object.keys(clean).length === 0) localStorage.removeItem(DPAD_OFFSETS_KEY);
    else localStorage.setItem(DPAD_OFFSETS_KEY, JSON.stringify(clean));
}

function readLayout3Pos(): Layout3Pos | null {
    try {
        const raw = localStorage.getItem(DPAD_LAYOUT3_POS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
}

function persistLayout3Pos(pos: Layout3Pos) {
    localStorage.setItem(DPAD_LAYOUT3_POS_KEY, JSON.stringify(pos));
}

function defaultLayout3Pos(): Layout3Pos {
    const b = LAYOUT3_SAFE_PX;
    const step = LAYOUT3_BTN_PX + LAYOUT3_GAP_PX;
    const scale = readScale();
    const btn = LAYOUT3_BTN_PX * scale;
    const gap = LAYOUT3_GAP_PX * scale;
    const s = btn + gap;
    return {
        left: { left: b, bottom: b },
        right: { left: b + s, bottom: b },
        up: { right: b, bottom: b },
        down: { right: b + s, bottom: b },
        "up-left": { right: b + s, bottom: b + s },
        "up-right": { right: Math.max(4, b - gap), bottom: b + s },
        "down-left": { right: b + s * 2, bottom: b },
        "down-right": { right: Math.max(4, b - gap), bottom: b },
    };
}

function layout3PosResolved(): Layout3Pos {
    return { ...defaultLayout3Pos(), ...(readLayout3Pos() ?? {}) };
}

function readDpadInsetFromStorage(layout: DpadLayoutId) {
    const b = localStorage.getItem(DPAD_INSET_B_KEY);
    const bottom = b != null && Number.isFinite(Number(b)) ? Number(b) : null;
    if (layout === 2) {
        const l = localStorage.getItem(DPAD_INSET_L_KEY);
        const left = l != null && Number.isFinite(Number(l)) ? Number(l) : null;
        if (left == null || bottom == null) return null;
        return { left, bottom };
    }
    const r = localStorage.getItem(DPAD_INSET_R_KEY);
    const right = r != null && Number.isFinite(Number(r)) ? Number(r) : null;
    if (right == null || bottom == null) return null;
    return { right, bottom };
}

function applyButtonOffsets(el: HTMLElement, offsets: OffsetMap) {
    for (const id of ALL_BUTTON_IDS) {
        const btn = el.querySelector(`.touch-dpad-${id}`);
        if (!(btn instanceof HTMLElement)) continue;
        const o = offsets[id];
        if (o && (o.x || o.y)) btn.style.transform = `translate(${Math.round(o.x)}px, ${Math.round(o.y)}px)`;
        else btn.style.transform = "";
    }
}

function clearInlineButtonPositions(el: HTMLElement) {
    for (const id of ALL_BUTTON_IDS) {
        const btn = el.querySelector(`.touch-dpad-${id}`);
        if (!(btn instanceof HTMLElement)) continue;
        btn.style.left = "";
        btn.style.right = "";
        btn.style.bottom = "";
        btn.style.top = "";
    }
}

function applyLayout3ButtonPos(btn: HTMLElement, id: DpadButtonId, p: { left?: number; right?: number; bottom: number }, offsets: OffsetMap) {
    btn.style.bottom = `${Math.round(p.bottom)}px`;
    if (p.left != null) {
        btn.style.left = `${Math.round(p.left)}px`;
        btn.style.right = "auto";
    } else if (p.right != null) {
        btn.style.right = `${Math.round(p.right)}px`;
        btn.style.left = "auto";
    }
    const o = offsets[id];
    if (o && (o.x || o.y)) btn.style.transform = `translate(${Math.round(o.x)}px, ${Math.round(o.y)}px)`;
    else btn.style.transform = "";
}

export function applyDpadLayoutFromStorage() {
    const el = getTouchDpadEl();
    if (!el) return;
    const layout = readLayout();
    const offsets = readOffsets();
    el.dataset.dpadLayout = String(layout);
    applyScaleToEl(el);
    applyOpacityToEl(el);
    syncDpadEightDirClass();
    applyButtonOffsets(el, offsets);

    if (layout === 3) {
        el.style.right = "";
        el.style.bottom = "";
        el.style.left = "";
        el.style.top = "";
        el.style.width = "";
        el.style.height = "";
        clearInlineButtonPositions(el);
        const pos = layout3PosResolved();
        for (const id of ALL_BUTTON_IDS) {
            const btn = el.querySelector(`.touch-dpad-${id}`);
            if (!(btn instanceof HTMLElement)) continue;
            if (!settingDpadEightDirections && DPAD_DIAG_DIRS.includes(id as DpadDiagDir)) {
                btn.style.display = "none";
                continue;
            }
            btn.style.display = "";
            const p = pos[id];
            if (!p) continue;
            applyLayout3ButtonPos(btn, id, p, offsets);
        }
        return;
    }

    clearInlineButtonPositions(el);
    const w = el.offsetWidth || 180;
    const h = el.offsetHeight || 180;
    const stored = readDpadInsetFromStorage(layout);
    if (!stored) {
        el.style.right = "";
        el.style.bottom = "";
        el.style.left = "";
        el.style.top = "";
    } else {
        const maxB = Math.max(0, innerHeight - h);
        const b = clamp(stored.bottom, 0, maxB);
        el.style.top = "auto";
        el.style.bottom = `${Math.round(b)}px`;
        if (layout === 2) {
            const maxL = Math.max(0, innerWidth - w);
            el.style.left = `${Math.round(clamp(stored.left!, 0, maxL))}px`;
            el.style.right = "auto";
        } else {
            const maxR = Math.max(0, innerWidth - w);
            el.style.right = `${Math.round(clamp(stored.right!, 0, maxR))}px`;
            el.style.left = "auto";
        }
    }
    refreshDpadGridOverlay();
    if (dpadPositionCustomizeMode) refreshDpadCustomizePositionPanel();
}

export function persistDpadPositionFromElement(skipSnap = false) {
    const el = getTouchDpadEl();
    if (!el) return;
    if (!skipSnap && readGridEnabled()) snapAllDpadButtonsToGrid();
    const layout = readLayout();
    if (layout === 3) {
        const pos: Layout3Pos = {};
        for (const id of ALL_BUTTON_IDS) {
            const btn = el.querySelector(`.touch-dpad-${id}`);
            if (!(btn instanceof HTMLElement) || btn.style.display === "none") continue;
            const rect = btn.getBoundingClientRect();
            const entry: { left?: number; right?: number; bottom: number } = {
                bottom: clamp(Math.round(innerHeight - rect.bottom), 0, innerHeight),
            };
            if (id === "left" || id === "right" || id === "down-left" || id === "down-right") {
                entry.left = clamp(Math.round(rect.left), 0, innerWidth);
            } else {
                entry.right = clamp(Math.round(innerWidth - rect.right), 0, innerWidth);
            }
            pos[id] = entry;
        }
        persistLayout3Pos(pos);
        persistOffsets(readOffsetsFromDom(el));
    } else {
    const rect = el.getBoundingClientRect();
    localStorage.setItem(DPAD_INSET_B_KEY, String(clamp(Math.round(innerHeight - rect.bottom), 0, innerHeight)));
    if (layout === 2) {
        localStorage.setItem(DPAD_INSET_L_KEY, String(clamp(Math.round(rect.left), 0, innerWidth)));
    } else {
        localStorage.setItem(DPAD_INSET_R_KEY, String(clamp(Math.round(innerWidth - rect.right), 0, innerWidth)));
    }
    persistOffsets(readOffsetsFromDom(el));
    }
    if (dpadPositionCustomizeMode) syncDpadCustomizePositionInputs();
}

function readOffsetsFromDom(el: HTMLElement): OffsetMap {
    const offsets: OffsetMap = {};
    for (const id of ALL_BUTTON_IDS) {
        const btn = el.querySelector(`.touch-dpad-${id}`);
        if (!(btn instanceof HTMLElement)) continue;
        const tr = btn.style.transform;
        const m = /translate\(\s*(-?[\d.]+)px,\s*(-?[\d.]+)px\s*\)/.exec(tr);
        if (m) offsets[id] = { x: Number(m[1]), y: Number(m[2]) };
    }
    return offsets;
}

function measureDpadInsetPx() {
    const el = getTouchDpadEl();
    if (!el) return { right: 12, bottom: 12, left: 12 };
    const rect = el.getBoundingClientRect();
    return {
        right: Math.max(0, innerWidth - rect.right),
        bottom: Math.max(0, innerHeight - rect.bottom),
        left: Math.max(0, rect.left),
    };
}

function expandedRect(rect: DOMRect, expand = HIT_EXPAND_PX): DOMRect {
    return new DOMRect(rect.left - expand, rect.top - expand, rect.width + expand * 2, rect.height + expand * 2);
}

function pointInRect(x: number, y: number, rect: DOMRect) {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function emptyHold(): Record<DpadDir, boolean> {
    return { up: false, down: false, left: false, right: false };
}

function holdFromButtonId(id: DpadButtonId): Record<DpadDir, boolean> {
    const out = emptyHold();
    if (DPAD_DIRS.includes(id as DpadDir)) {
        out[id as DpadDir] = true;
        return out;
    }
    const pair = DIAG_TO_CARDINAL[id as DpadDiagDir];
    out[pair[0]] = true;
    out[pair[1]] = true;
    return out;
}


function resolveCardinalOnly(hits: DpadDir[], clientX: number, clientY: number): Record<DpadDir, boolean> {
    const out = emptyHold();
    if (hits.length === 0) return out;
    let best = hits[0];
    let bestDist = Infinity;
    const el = getTouchDpadEl();
    for (const d of hits) {
        const btn = el?.querySelector(`.touch-dpad-${d}`);
        if (!(btn instanceof HTMLElement)) continue;
        const r = btn.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dist = (clientX - cx) ** 2 + (clientY - cy) ** 2;
        if (dist < bestDist) {
            bestDist = dist;
            best = d;
        }
    }
    out[best] = true;
    return out;
}

function hitTestDpadAt(clientX: number, clientY: number): Record<DpadDir, boolean> {
    const el = getTouchDpadEl();
    if (!el) return emptyHold();

    if (settingDpadEightDirections) {
        for (const id of DPAD_DIAG_DIRS) {
            const btn = el.querySelector(`.touch-dpad-${id}`);
            if (!(btn instanceof HTMLElement) || btn.style.display === "none") continue;
            if (pointInRect(clientX, clientY, expandedRect(btn.getBoundingClientRect(), HIT_EXPAND_PX))) {
                return holdFromButtonId(id);
            }
        }
    }

    const cardinalHits: DpadDir[] = [];
    for (const d of DPAD_DIRS) {
        const btn = el.querySelector(`.touch-dpad-${d}`);
        if (!(btn instanceof HTMLElement)) continue;
        if (pointInRect(clientX, clientY, expandedRect(btn.getBoundingClientRect()))) cardinalHits.push(d);
    }

    if (!settingDpadEightDirections) {
        const out = emptyHold();
        for (const d of cardinalHits) out[d] = true;
        return out;
    }

    if (cardinalHits.length === 0) return emptyHold();
    return resolveCardinalOnly(cardinalHits, clientX, clientY);
}

function syncDpadHoldVisual() {
    const el = getTouchDpadEl();
    if (!el) return;
    for (const d of DPAD_DIRS) {
        const btn = el.querySelector(`.touch-dpad-${d}`);
        if (btn instanceof HTMLElement) btn.classList.toggle("touch-dpad-btn--pressed", dpadHold[d]);
    }
    if (settingDpadEightDirections) {
        for (const id of DPAD_DIAG_DIRS) {
            const btn = el.querySelector(`.touch-dpad-${id}`);
            if (!(btn instanceof HTMLElement) || btn.style.display === "none") continue;
            const pair = DIAG_TO_CARDINAL[id];
            btn.classList.toggle("touch-dpad-btn--pressed", dpadHold[pair[0]] && dpadHold[pair[1]]);
        }
    }
}

function applyDpadHold(next: Record<DpadDir, boolean>) {
    let changed = false;
    for (const d of DPAD_DIRS) {
        if (dpadHold[d] !== next[d]) {
            dpadHold[d] = next[d];
            changed = true;
        }
    }
    if (changed) syncDpadHoldVisual();
}

function recomputeDpadHoldFromPointers() {
    const combined = emptyHold();
    for (const hold of dpadPointerHolds.values()) {
        for (const d of DPAD_DIRS) {
            if (hold[d]) combined[d] = true;
        }
    }
    applyDpadHold(combined);
}

function clearDpadHold() {
    dpadPointerHolds.clear();
    applyDpadHold(emptyHold());
}

function holdFromPointerTarget(target: EventTarget | null): Record<DpadDir, boolean> | null {
    if (!(target instanceof HTMLElement)) return null;
    const btn = target.closest(".touch-dpad-btn");
    if (!(btn instanceof HTMLElement) || btn.style.display === "none") return null;
    const id = buttonIdFromEl(btn);
    if (!id) return null;
    if (!settingDpadEightDirections && DPAD_DIAG_DIRS.includes(id as DpadDiagDir)) return null;
    return holdFromButtonId(id);
}

function setDpadPointerHold(pointerId: number, clientX: number, clientY: number, target?: EventTarget | null) {
    const fromTarget = target != null ? holdFromPointerTarget(target) : null;
    dpadPointerHolds.set(pointerId, fromTarget ?? hitTestDpadAt(clientX, clientY));
    recomputeDpadHoldFromPointers();
}

function releaseDpadPointer(pointerId: number) {
    if (!dpadPointerHolds.delete(pointerId)) return;
    recomputeDpadHoldFromPointers();
}

function pointerDistance(a: { x: number; y: number }, b: { x: number; y: number }) {
    return Math.hypot(a.x - b.x, a.y - b.y);
}

function applyScaleVisual(scale: number) {
    const s = clamp(scale, DPAD_SCALE_MIN, DPAD_SCALE_MAX);
    for (const root of getDpadRoots()) root.style.setProperty("--dpad-scale", String(s));
}

function persistDpadScale(scale: number) {
    const s = clamp(scale, DPAD_SCALE_MIN, DPAD_SCALE_MAX);
    localStorage.setItem(DPAD_SCALE_KEY, String(Math.round(s * 100) / 100));
    applyDpadLayoutFromStorage();
    syncDpadCustomizeScaleSlider();
    if (readGridEnabled()) {
        snapAllDpadButtonsToGrid();
        persistDpadPositionFromElement(true);
    }
    refreshDpadGridOverlay();
}

export function scaleToSliderValue(scale: number) {
    return Math.round(clamp(scale, DPAD_SCALE_MIN, DPAD_SCALE_MAX) * 100);
}

export function setDpadScaleFromSliderValue(v: number) {
    persistDpadScale(clamp(v / 100, DPAD_SCALE_MIN, DPAD_SCALE_MAX));
}

export function opacityToSliderValue(opacity: number) {
    return Math.round(clamp(opacity, DPAD_OPACITY_MIN, DPAD_OPACITY_MAX) * 100);
}

export function setDpadOpacityFromSliderValue(v: number) {
    const o = clamp(v / 100, DPAD_OPACITY_MIN, DPAD_OPACITY_MAX);
    localStorage.setItem(DPAD_OPACITY_KEY, String(Math.round(o * 100)));
    for (const root of getDpadRoots()) applyOpacityToEl(root);
    syncDpadCustomizeOpacitySlider(o);
}

export function syncDpadCustomizeOpacitySlider(opacity?: number) {
    const slider = document.getElementById("dpadCustomizeOpacitySlider") as HTMLInputElement | null;
    if (!slider) return;
    const o = opacity ?? readOpacity();
    slider.value = String(opacityToSliderValue(o));
}

function syncDpadSelectionVisual() {
    const el = getTouchDpadEl();
    if (!el) return;
    for (const id of ALL_BUTTON_IDS) {
        const btn = el.querySelector(`.touch-dpad-${id}`);
        if (btn instanceof HTMLElement) {
            btn.classList.toggle("touch-dpad-btn--selected", selectedDpadButtons.has(id));
        }
    }
}

function clearDpadMultiSelect() {
    dpadMultiSelectMode = false;
    selectedDpadButtons.clear();
    syncDpadSelectionVisual();
    document.body.classList.remove("dpad-multi-select-mode");
    refreshDpadCustomizePositionPanel();
}

function markDpadDragUndo() {
    if (!dpadDragUndoPushed) {
        pushDpadCustomizeUndoBeforeChange();
        dpadDragUndoPushed = true;
    }
}

function isDpadCustomizeEmptyTarget(target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) return false;
    if (target.closest(".touch-dpad-btn")) return false;
    if (target.closest(".touch-dpad-drag-handle")) return false;
    if (target.closest(".dpad-customize-toolbar")) return false;
    if (target.closest(".dpad-customize-return")) return false;
    if (target.closest(".dpadCustomizeExitConfirm")) return false;
    return true;
}

function captureDragStartState(el: HTMLElement, ids: Iterable<DpadButtonId>) {
    const ins = measureDpadInsetPx();
    dpadDrag.startRight = ins.right;
    dpadDrag.startBottom = ins.bottom;
    dpadDrag.startLeft = ins.left;
    dpadDrag.startOffsets = readOffsetsFromDom(el);
    if (readLayout() === 3) {
        const pos: Layout3Pos = {};
        for (const id of ids) {
            const btn = el.querySelector(`.touch-dpad-${id}`);
            if (!(btn instanceof HTMLElement) || btn.style.display === "none") continue;
            const rect = btn.getBoundingClientRect();
            const entry: { left?: number; right?: number; bottom: number } = {
                bottom: innerHeight - rect.bottom,
            };
            if (id === "left" || id === "right" || id === "down-left" || id === "down-right") entry.left = rect.left;
            else entry.right = innerWidth - rect.right;
            pos[id] = entry;
        }
        dpadDrag.startLayout3 = pos;
    }
}

export function syncDpadCustomizeScaleSlider(scale?: number) {
    const slider = document.getElementById("dpadCustomizeScaleSlider") as HTMLInputElement | null;
    if (!slider) return;
    const s = scale ?? readScale();
    slider.value = String(scaleToSliderValue(s));
}

export function beginDpadCustomizeSession() {
    const snap: Record<string, string | null> = {};
    for (const k of DPAD_CONFIG_KEYS) snap[k] = localStorage.getItem(k);
    dpadCustomizeSnapshot = snap;
    dpadUndoStack = [];
    dpadRedoStack = [];
    syncDpadCustomizeScaleSlider();
    syncDpadCustomizeOpacitySlider();
    refreshDpadGridBtn();
    syncDpadCustomizeUndoRedoButtons();
}

export function commitDpadCustomizeSession() {
    dpadCustomizeSnapshot = null;
}

export function discardDpadCustomizeSession() {
    if (!dpadCustomizeSnapshot) return;
    for (const k of DPAD_CONFIG_KEYS) {
        const v = dpadCustomizeSnapshot[k];
        if (v == null) localStorage.removeItem(k);
        else localStorage.setItem(k, v);
    }
    dpadCustomizeSnapshot = null;
    reloadDpadSettingsFromStorage();
    applyDpadLayoutFromStorage();
    syncDpadCustomizeScaleSlider();
    syncDpadCustomizeOpacitySlider();
}

function onDpadPinchMove() {
    if (!dpadPinch.active || dpadPinch.pointers.size < 2) return;
    const pts = [...dpadPinch.pointers.values()];
    const dist = pointerDistance(pts[0], pts[1]);
    if (dpadPinch.startDist <= 0) return;
    dpadPinch.currentScale = clamp(dpadPinch.startScale * (dist / dpadPinch.startDist), DPAD_SCALE_MIN, DPAD_SCALE_MAX);
    applyScaleVisual(dpadPinch.currentScale);
}

function trackDpadPinchPointer(e: PointerEvent, on: boolean) {
    if (!dpadPositionCustomizeMode) return;
    if (on) dpadPinch.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    else dpadPinch.pointers.delete(e.pointerId);

    if (dpadPinch.pointers.size === 2) {
        cancelMarqueeSelect();
        pendingCustomizeDrag.active = false;
        const pts = [...dpadPinch.pointers.values()];
        if (!dpadPinch.active) {
            if (!dpadPinchUndoPushed) {
                pushDpadCustomizeUndoBeforeChange();
                dpadPinchUndoPushed = true;
            }
            dpadPinch.active = true;
            dpadPinch.startDist = pointerDistance(pts[0], pts[1]);
            dpadPinch.startScale = readScale();
            dpadPinch.currentScale = dpadPinch.startScale;
        }
        onDpadPinchMove();
        syncDpadCustomizeScaleSlider(dpadPinch.currentScale);
    } else if (dpadPinch.pointers.size < 2) {
        if (dpadPinch.active) {
            persistDpadScale(dpadPinch.currentScale);
            dpadPinch.active = false;
            dpadPinchUndoPushed = false;
        }
        dpadPinch.startDist = 0;
    }
}

function tryStartPendingCustomizeDrag(e: PointerEvent) {
    if (!pendingCustomizeDrag.active || e.pointerId !== pendingCustomizeDrag.pointerId) return false;
    const dist = Math.hypot(e.clientX - pendingCustomizeDrag.startX, e.clientY - pendingCustomizeDrag.startY);
    if (dist < CUSTOMIZE_DRAG_THRESHOLD_PX) return false;
    const mode = pendingCustomizeDrag.mode;
    pendingCustomizeDrag.active = false;
    if (!mode) return false;
    if (dpadPointerHolds.has(e.pointerId)) releaseDpadPointer(e.pointerId);
    markDpadDragUndo();
    dpadDrag.active = true;
    dpadDrag.pointerId = e.pointerId;
    dpadDrag.startX = pendingCustomizeDrag.startX;
    dpadDrag.startY = pendingCustomizeDrag.startY;
    const el = getTouchDpadEl();
    if (!el) return true;

    const btnMode = mode !== "group" ? (mode as DpadButtonId) : null;
    if (btnMode && selectedDpadButtons.size > 0 && !selectedDpadButtons.has(btnMode)) {
        clearDpadMultiSelect();
    }

    const useMulti =
        btnMode != null && selectedDpadButtons.size >= 1 && selectedDpadButtons.has(btnMode);

    if (useMulti) {
        dpadDrag.mode = "multi";
        captureDragStartState(el, selectedDpadButtons);
    } else {
        dpadDrag.mode = mode;
        if (mode === "group") captureDragStartState(el, ALL_BUTTON_IDS);
        else captureDragStartState(el, [mode as DpadButtonId]);
    }

    const target =
        mode === "group"
            ? el.querySelector(".touch-dpad-drag-handle")
            : el.querySelector(`.touch-dpad-${mode}`);
    if (target instanceof HTMLElement) {
        try {
            target.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    }
    onDpadDragMove(e);
    return true;
}

function onDpadDragMove(e: PointerEvent) {
    if (dpadPositionCustomizeMode) {
        tryStartPendingCustomizeDrag(e);
        if (dpadPinch.active && dpadPinch.pointers.size >= 2) {
            onDpadPinchMove();
            return;
        }
    }

    if (!dpadDrag.active || e.pointerId !== dpadDrag.pointerId) return;
    const el = getTouchDpadEl();
    if (!el) return;
    const layout = readLayout();
    const dx = e.clientX - dpadDrag.startX;
    const dy = e.clientY - dpadDrag.startY;

    if (dpadDrag.mode === "group") {
        if (layout === 3) {
            for (const id of ALL_BUTTON_IDS) {
                const btn = el.querySelector(`.touch-dpad-${id}`);
                if (!(btn instanceof HTMLElement) || btn.style.display === "none") continue;
                const start = dpadDrag.startLayout3[id];
                if (!start) continue;
                if (start.left != null) btn.style.left = `${Math.round(start.left + dx)}px`;
                if (start.right != null) btn.style.right = `${Math.round(start.right - dx)}px`;
                btn.style.bottom = `${Math.round(start.bottom - dy)}px`;
            }
            syncDpadCustomizePositionInputs();
            return;
        }
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        const maxB = Math.max(0, innerHeight - h);
        el.style.bottom = `${Math.round(clamp(dpadDrag.startBottom - dy, 0, maxB))}px`;
        if (layout === 2) {
            const maxL = Math.max(0, innerWidth - w);
            el.style.left = `${Math.round(clamp(dpadDrag.startLeft + dx, 0, maxL))}px`;
        } else {
            const maxR = Math.max(0, innerWidth - w);
            el.style.right = `${Math.round(clamp(dpadDrag.startRight - dx, 0, maxR))}px`;
        }
        syncDpadCustomizePositionInputs();
        return;
    }

    if (dpadDrag.mode === "multi") {
        if (layout === 3) {
            for (const id of selectedDpadButtons) {
                const btn = el.querySelector(`.touch-dpad-${id}`);
                if (!(btn instanceof HTMLElement) || btn.style.display === "none") continue;
                const start = dpadDrag.startLayout3[id];
                if (!start) continue;
                if (start.left != null) btn.style.left = `${Math.round(start.left + dx)}px`;
                if (start.right != null) btn.style.right = `${Math.round(start.right - dx)}px`;
                btn.style.bottom = `${Math.round(start.bottom - dy)}px`;
            }
            syncDpadCustomizePositionInputs(selectedDpadButtons);
            return;
        }
        for (const id of selectedDpadButtons) {
            const btn = el.querySelector(`.touch-dpad-${id}`);
            if (!(btn instanceof HTMLElement)) continue;
            const start = dpadDrag.startOffsets[id] ?? { x: 0, y: 0 };
            btn.style.transform = `translate(${Math.round(start.x + dx)}px, ${Math.round(start.y + dy)}px)`;
        }
        syncDpadCustomizePositionInputs(selectedDpadButtons);
        return;
    }

    const btn = el.querySelector(`.touch-dpad-${dpadDrag.mode}`);
    if (!(btn instanceof HTMLElement)) return;
    const start = dpadDrag.startOffsets[dpadDrag.mode] ?? { x: 0, y: 0 };
    btn.style.transform = `translate(${Math.round(start.x + dx)}px, ${Math.round(start.y + dy)}px)`;
    syncDpadCustomizePositionInputs(
        dpadDrag.mode === "multi" ? selectedDpadButtons : [dpadDrag.mode as DpadButtonId],
    );
}

function onDpadDragEnd(e: PointerEvent) {
    if (dpadPositionCustomizeMode && e.pointerType !== "mouse") trackDpadPinchPointer(e, false);
    if (!dpadDrag.active || e.pointerId !== dpadDrag.pointerId) return;
    dpadDrag.active = false;
    dpadDrag.pointerId = -1;
    dpadDragUndoPushed = false;
    persistDpadPositionFromElement();
}

function startDpadCustomizeDrag(e: PointerEvent, mode: "group" | DpadButtonId) {
    if (dpadPinch.pointers.size >= 2) return;
    const el = getTouchDpadEl();
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    markDpadDragUndo();
    dpadDrag.active = true;
    dpadDrag.pointerId = e.pointerId;
    dpadDrag.mode = mode;
    dpadDrag.startX = e.clientX;
    dpadDrag.startY = e.clientY;
    captureDragStartState(el, mode === "group" ? ALL_BUTTON_IDS : [mode]);
    const target =
        mode === "group" ? el.querySelector(".touch-dpad-drag-handle") : el.querySelector(`.touch-dpad-${mode}`);
    if (target instanceof HTMLElement) {
        try {
            target.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    }
}

function onDpadTouchMove(e: PointerEvent) {
    if (!dpadPointerHolds.has(e.pointerId)) return;
    if (!dpadPlayGate() || !dpadTouchUiGate()) return;
    setDpadPointerHold(e.pointerId, e.clientX, e.clientY);
}

function onDpadTouchEnd(e: PointerEvent) {
    releaseDpadPointer(e.pointerId);
}

function beginDpadPointer(e: PointerEvent, dpad: HTMLElement) {
    e.preventDefault();
    try {
        dpad.setPointerCapture(e.pointerId);
    } catch {
        /* ignore */
    }
    setDpadPointerHold(e.pointerId, e.clientX, e.clientY, e.target);
}

function bindDpadPlayTouch(layer: HTMLElement) {
    const dpad = layer.querySelector(".touch-dpad");
    if (!(dpad instanceof HTMLElement)) return;

    dpad.addEventListener("pointerdown", (e) => {
        if (dpadPositionCustomizeMode) return;
        if (!dpadPlayGate() || !dpadTouchUiGate()) return;
        const target = e.target;
        if (!(target instanceof HTMLElement)) return;
        if (target.classList.contains("touch-dpad-drag-handle")) return;
        if (!target.closest(".touch-dpad-btn")) return;
        beginDpadPointer(e, dpad);
    });

    dpad.addEventListener("pointermove", onDpadTouchMove);
    dpad.addEventListener("pointerup", onDpadTouchEnd);
    dpad.addEventListener("pointercancel", onDpadTouchEnd);
    dpad.addEventListener("lostpointercapture", (e) => {
        if (dpadPointerHolds.has(e.pointerId)) onDpadTouchEnd(e);
    });
}

function bindDpadCustomizeDrag(layer: HTMLElement) {
    if (layer.dataset.dpadCustomizeBound === "1") return;
    layer.dataset.dpadCustomizeBound = "1";

    layer.addEventListener(
        "pointerdown",
        (e) => {
            if (!dpadPositionCustomizeMode) return;
            if (!(e.target instanceof HTMLElement)) return;
            if (dpadPinch.pointers.size >= 2) return;

            if (e.target.closest(".touch-dpad-drag-handle")) {
                startDpadCustomizeDrag(e, "group");
                return;
            }

            const btn = e.target.closest(".touch-dpad-btn");
            if (!(btn instanceof HTMLElement)) return;
            const id = buttonIdFromEl(btn);
            if (!id) return;
            e.preventDefault();
            pendingCustomizeDrag.active = true;
            pendingCustomizeDrag.pointerId = e.pointerId;
            pendingCustomizeDrag.mode = id;
            pendingCustomizeDrag.startX = e.clientX;
            pendingCustomizeDrag.startY = e.clientY;
        },
        true,
    );
}

export function setupDpadTouchControls(touchSteerLayer: HTMLElement | null, gates: { isPlaying: () => boolean; isTouchUi: () => boolean }) {
    dpadPlayGate = gates.isPlaying;
    dpadTouchUiGate = gates.isTouchUi;
    if (!touchSteerLayer) return;
    bindDpadPlayTouch(touchSteerLayer);
    bindDpadCustomizeDrag(touchSteerLayer);

    const onCustomizePointerDown = (e: PointerEvent) => {
        if (!dpadPositionCustomizeMode) return;
        if (isDpadCustomizeEmptyTarget(e.target) && dpadPinch.pointers.size === 0) {
            cancelMarqueeSelect();
            marqueeSelect.active = true;
            marqueeSelect.dragging = false;
            marqueeSelect.pointerId = e.pointerId;
            marqueeSelect.startX = e.clientX;
            marqueeSelect.startY = e.clientY;
        }
        if (e.pointerType !== "mouse") trackDpadPinchPointer(e, true);
    };
    const onCustomizePointerMove = (e: PointerEvent) => {
        if (!dpadPositionCustomizeMode) return;
        if (marqueeSelect.active && e.pointerId === marqueeSelect.pointerId) {
            const dist = Math.hypot(e.clientX - marqueeSelect.startX, e.clientY - marqueeSelect.startY);
            if (dist > CUSTOMIZE_DRAG_THRESHOLD_PX) {
                marqueeSelect.dragging = true;
                updateMarqueeRect(marqueeSelect.startX, marqueeSelect.startY, e.clientX, e.clientY);
            }
        }
        if (e.pointerType !== "mouse") trackDpadPinchPointer(e, true);
    };
    const onCustomizePointerUp = (e: PointerEvent) => {
        if (!dpadPositionCustomizeMode) return;
        if (marqueeSelect.active && e.pointerId === marqueeSelect.pointerId) {
            if (marqueeSelect.dragging) {
                selectButtonsInMarquee(marqueeSelect.startX, marqueeSelect.startY, e.clientX, e.clientY);
            } else if (isDpadCustomizeEmptyTarget(e.target)) {
                clearDpadMultiSelect();
            }
            cancelMarqueeSelect();
        }
        if (e.pointerType !== "mouse") trackDpadPinchPointer(e, false);
        if (pendingCustomizeDrag.active && e.pointerId === pendingCustomizeDrag.pointerId) {
            const mode = pendingCustomizeDrag.mode;
            const dist = Math.hypot(e.clientX - pendingCustomizeDrag.startX, e.clientY - pendingCustomizeDrag.startY);
            if (mode && mode !== "group" && dist < CUSTOMIZE_DRAG_THRESHOLD_PX) {
                selectDpadButtonOnTap(mode, e.shiftKey);
            }
            pendingCustomizeDrag.active = false;
        }
    };

    window.addEventListener("pointerdown", onCustomizePointerDown, true);
    window.addEventListener("pointermove", onCustomizePointerMove, true);
    window.addEventListener("pointerup", onCustomizePointerUp, true);
    window.addEventListener("pointercancel", onCustomizePointerUp, true);

    const onResizeGrid = () => {
        if (dpadPositionCustomizeMode) refreshDpadGridOverlay();
    };
    window.addEventListener("resize", onResizeGrid);

    window.addEventListener("pointermove", (e) => {
        onDpadDragMove(e);
        if (dpadPointerHolds.has(e.pointerId)) onDpadTouchMove(e);
    });
    window.addEventListener("pointerup", (e) => {
        onDpadDragEnd(e);
        onDpadTouchEnd(e);
    });
    window.addEventListener("pointercancel", (e) => {
        onDpadDragEnd(e);
        onDpadTouchEnd(e);
    });
    bindDpadCustomizePositionPanel();
}

export function setDpadCustomizeMode(on: boolean) {
    dpadPositionCustomizeMode = on;
    document.body.classList.toggle("dpad-customize-mode", on);
    const toolbar = document.getElementById("dpadCustomizeToolbar");
    if (toolbar) toolbar.classList.toggle("hidden", !on);
    const ret = document.getElementById("dpadCustomizeReturnBtn");
    if (ret) ret.classList.toggle("hidden", !on);
    if (on) {
        syncDpadCustomizeScaleSlider();
        syncDpadCustomizeOpacitySlider();
        refreshDpadGridBtn();
        refreshDpadGridOverlay();
        syncDpadCustomizeUndoRedoButtons();
        bindDpadCustomizePositionPanel();
        refreshDpadCustomizePositionPanel();
    }
    if (!on) {
        dpadPinch.active = false;
        dpadPinch.pointers.clear();
        dpadPinchUndoPushed = false;
        pendingCustomizeDrag.active = false;
        cancelMarqueeSelect();
        clearDpadMultiSelect();
        dpadDragUndoPushed = false;
        dpadUndoStack = [];
        dpadRedoStack = [];
        clearDpadHold();
        const gridOverlay = document.getElementById("dpadCustomizeGridOverlay");
        if (gridOverlay) gridOverlay.classList.add("hidden");
    }
}

export function getDpadLayout(): DpadLayoutId {
    return readLayout();
}

export function setDpadLayout(layout: DpadLayoutId) {
    localStorage.setItem(DPAD_LAYOUT_KEY, String(layout));
    applyDpadLayoutFromStorage();
    updateLayoutButtonLabel();
    if (readGridEnabled()) {
        requestAnimationFrame(() => {
            snapAllDpadButtonsToGrid();
            persistDpadPositionFromElement(true);
        });
    }
}

export function cycleDpadLayout() {
    const cur = readLayout();
    setDpadLayout(cur === 1 ? 2 : cur === 2 ? 3 : 1);
}

function updateLayoutButtonLabel() {
    const btn = document.getElementById("dpadCustomizeLayoutBtn");
    if (btn) btn.textContent = `Layout ${readLayout()}`;
}

export function resetDpadToDefaults() {
    localStorage.removeItem(DPAD_INSET_R_KEY);
    localStorage.removeItem(DPAD_INSET_B_KEY);
    localStorage.removeItem(DPAD_INSET_L_KEY);
    localStorage.removeItem(DPAD_OFFSETS_KEY);
    localStorage.removeItem(DPAD_LAYOUT3_POS_KEY);
    localStorage.removeItem(DPAD_SCALE_KEY);
    localStorage.removeItem(DPAD_OPACITY_KEY);
    localStorage.setItem(DPAD_LAYOUT_KEY, "1");
    localStorage.removeItem(DPAD_GRID_KEY);
    applyDpadLayoutFromStorage();
    updateLayoutButtonLabel();
    refreshDpadGridBtn();
    refreshDpadGridOverlay();
}

export function setDpadEightDirections(on: boolean) {
    settingDpadEightDirections = on;
    localStorage.setItem(DPAD_EIGHT_DIR_KEY, on ? "true" : "false");
    applyDpadLayoutFromStorage();
    refreshDpadEightDirBtn();
}

function refreshDpadEightDirBtn() {
    syncToggleSwitch(document.getElementById("toggleDpadEightDirBtn"), settingDpadEightDirections);
}

export function refreshDpadSettingsUI() {
    refreshDpadEightDirBtn();
    updateLayoutButtonLabel();
}

export function resetDpadHoldState() {
    clearDpadHold();
}

/** Write current D-Pad layout/position into localStorage for Settings Copy. */
export function persistDpadSettingsForShare(): void {
    const el = getTouchDpadEl();
    if (el) persistDpadPositionFromElement(true);
    localStorage.setItem(DPAD_LAYOUT_KEY, String(readLayout()));
    localStorage.setItem(DPAD_SCALE_KEY, String(readScale()));
    localStorage.setItem(DPAD_OPACITY_KEY, String(Math.round(readOpacity() * 100)));
    localStorage.setItem(DPAD_EIGHT_DIR_KEY, settingDpadEightDirections ? "true" : "false");
    localStorage.setItem(DPAD_GRID_KEY, readGridEnabled() ? "true" : "false");
    localStorage.setItem(DPAD_OFFSETS_KEY, JSON.stringify(readOffsets()));
    localStorage.setItem(DPAD_LAYOUT3_POS_KEY, JSON.stringify(layout3PosResolved()));
    if (localStorage.getItem(DPAD_INSET_B_KEY) === null) localStorage.setItem(DPAD_INSET_B_KEY, "0");
    if (localStorage.getItem(DPAD_INSET_R_KEY) === null) localStorage.setItem(DPAD_INSET_R_KEY, "0");
    if (localStorage.getItem(DPAD_INSET_L_KEY) === null) localStorage.setItem(DPAD_INSET_L_KEY, "0");
}
